/** @format */

import React, { useEffect, useState, useRef } from "react";

import {
    withAuthenticator,
    WithAuthenticatorOptions,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

import "@xyflow/react/dist/style.css";
import "vis-network/styles/vis-network.min.css";

// Components
import SideNavBar from "src/components/core/App/SideNavBar";
import LoadingContainer, { LoadingContainerHandle } from "src/components/core/App/LoadingContainer";
import NotificationContainer, { NotificationContainerHandle } from "src/components/core/App/NotificationContainer";

// Services
import { TenantService } from "src/services/shared/tenant.service";
import { SubscriptionService } from "src/services/saas/subscription.service";
import { LicenseService } from "src/services/enterprise/license.service";

// Feature flags
import { featureFlags } from "src/settings/features";

// Enterprise Components
import { LicenseExpiredPage } from "src/components/enterprise";

// Store
import { useUserStore } from "src/stores/user.store";
import { useTenantStore } from "src/stores/tenant.store";
import { useAccountStore, useHasAnyAccount } from "src/stores/account.store";

// Routing
import { lazy, Suspense } from "react";
import { Routes, Route, useLocation, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import environment from "./settings/environmentLoader";

// Pages
const Network = lazy(() => import("./pages/Network"));
const GlobalNetwork = lazy(() => import("./pages/GlobalNetwork"));
const Serverless = lazy(() => import("./pages/Serverless"));
const Settings = lazy(() => import("./pages/Settings"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const NoMatch = lazy(() => import("./pages/NoMatch"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const GroupManagement = lazy(() => import("./pages/GroupManagement"));
const UserManagement = lazy(() => import("./pages/UserManagement"));

declare const window: any;

function App({ signOut, user }) {
    const location = useLocation();
    const navigate = useNavigate();

    // Store user in Zustand store
    const setUser = useUserStore(state => state.setUser);
    const getCurrentUser = useUserStore(state => state.getCurrentUser);
    const reset = useUserStore(state => state.reset);

    // Tenant context store
    const loadTenantContext = useTenantStore(state => state.loadTenantContext);
    const resetTenant = useTenantStore(state => state.reset);

    // Account store
    const loadAccounts = useAccountStore(state => state.loadAccounts);
    const hasAnyAccount = useHasAnyAccount();

    const [isAppReady, setIsAppReady] = useState(false);
    const [mustSubscribe, setMustSubscribe] = useState(false);
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);
    const [licenseExpired, setLicenseExpired] = useState(false);

    // Check license status independently of authentication
    useEffect(() => {
        const isLicenseExpired = LicenseService.shouldBlockAccess();
        setLicenseExpired(isLicenseExpired);
    }, []); // Run once on mount, independent of user authentication

    // Initialize user in store and load critical data after authentication
    // Note: withAuthenticator HOC guarantees user is authenticated when this component renders.
    // The CognitoUser object does NOT have an 'authenticated' property.
    useEffect(() => {
        if (user) {
            const initApp = async () => {
                // Extract username from various possible locations
                const username = user.username ||
                    user.signInUserSession?.idToken?.payload['cognito:username'] ||
                    user.signInUserSession?.idToken?.payload.sub ||
                    user.attributes?.email ||
                    user.attributes?.preferred_username;

                setUser({
                    username,
                    email: user.attributes?.email,
                    given_name: user.attributes?.given_name,
                    family_name: user.attributes?.family_name,
                    email_verified: user.attributes?.email_verified === 'true',
                    attributes: user.attributes
                });

                // Enrich user data in background (non-blocking)
                getCurrentUser();

                // Critical path: load tenant context first (needed for role-based account filtering)
                await loadTenantContext();

                // Then load accounts (backend filters by role after tenant context is set)
                await loadAccounts();

                setIsAppReady(true);
            };

            initApp();
        } else {
            setIsAppReady(false);
            reset();
            resetTenant();
            TenantService.clearCache();
            // Note: Don't reset license state here since it's independent of authentication
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (!user) {
        TenantService.clearCache();
    }

    // Load subscription plan once after authentication
    useEffect(() => {
        if (!isAppReady) return;

        if (featureFlags.isSubscriptionsEnabled()) {
            SubscriptionService.getSubscriptionPlan().then((response) => {
                setSubscriptionPlan(response);
                const isExpired = response.status === "EXPIRED";
                setMustSubscribe(isExpired);
            });
        } else {
            setMustSubscribe(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAppReady]);

    // Redirect to subscriptions page if subscription expired
    useEffect(() => {
        if (mustSubscribe && location.pathname !== '/subscriptions') {
            navigate('/subscriptions');
        }
    }, [mustSubscribe, location.pathname, navigate]);

    // Matomo - Only load once if enabled
    useEffect(() => {
        if (featureFlags.isAnalyticsEnabled() && environment.matomo.containerUrl) {
            var _mtm = window._mtm = window._mtm || [];
            _mtm.push({ 'mtm.startTime': (new Date().getTime()), 'event': 'mtm.Start' });

            var d = document;
            var g = d.createElement('script');
            g.async = true;
            g.src = environment.matomo.containerUrl;

            var s = d.getElementsByTagName('script')[0];
            s.parentNode.insertBefore(g, s);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadingRef = useRef<LoadingContainerHandle>(null);
    const currentLoadingId = useRef<string | null>(null);

    const openLoading = () => {
        // If already showing, don't open another
        if (currentLoadingId.current) return;

        // Show new loading
        currentLoadingId.current = loadingRef.current!.show({
            message: 'Loading...',
            type: 'spinner',
            overlay: true
        });
    };

    const closeLoading = () => {
        // Hide current loading if exists
        if (currentLoadingId.current) {
            loadingRef.current!.hide(currentLoadingId.current);
            currentLoadingId.current = null;
        }
    };

    const notificationRef = useRef<NotificationContainerHandle>(null);

    const toggleNotifier = (message: string, type: string, options?: { duration?: number; persistent?: boolean; html?: boolean }) => {
        notificationRef.current!.addNotification(message, type as any, options);
    };

    const updateSubscriptionPlan = (newSubscriptionPlan) => {
        setSubscriptionPlan(newSubscriptionPlan);
        const isExpired = newSubscriptionPlan.status === "EXPIRED";
        setMustSubscribe(isExpired);

        // If subscription becomes active, refresh account data
        if (!isExpired && mustSubscribe) {
            loadAccounts();
        }
    };

    const [queryParameters] = useSearchParams();
    const subscriptionParam = queryParameters.get("subscription");

    // Handle subscription success notification
    useEffect(() => {
        if (featureFlags.isSubscriptionsEnabled() && subscriptionParam === 'success' && notificationRef.current) {
            notificationRef.current.addNotification(
                'Thank you for your subscription!<br/>Start by setting up your first account.',
                'success',
                { html: true, duration: 8000 }
            );
        }
    }, [subscriptionParam, notificationRef]);

    // If license is expired, show the license expired page (regardless of authentication)
    if (licenseExpired) {
        return <LicenseExpiredPage signOut={signOut} />;
    }

    // Show loading screen while critical data is being fetched
    if (!isAppReady) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Loading your workspace...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <LoadingContainer ref={loadingRef} />

            <div className="row">
                <div className="col-md-3 col-xl-2 px-0 bg-vizcon leftPanel">
                    <SideNavBar signOut={signOut} subscriptionPlan={subscriptionPlan} toggleNotifier={toggleNotifier} />
                </div>

                <div className="col rightPanel">
                    <NotificationContainer ref={notificationRef} />

                    <Suspense
                        fallback={<div className="container">Loading...</div>}
                    >
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : hasAnyAccount ? (
                                        <Network
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                        />
                                    ) : (
                                        <Navigate to="/accounts" />
                                    )
                                }
                            />
                            <Route
                                path="/index.html"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : hasAnyAccount ? (
                                        <Network
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                        />
                                    ) : (
                                        <Navigate to="/accounts" />
                                    )
                                }
                            />
                            <Route
                                path="/global-network"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : hasAnyAccount ? (
                                        <GlobalNetwork
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                        />
                                    ) : (
                                        <Navigate to="/accounts" />
                                    )
                                }
                            />
                            <Route
                                path="/serverless"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : hasAnyAccount ? (
                                        <Serverless
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                            loadingRef={loadingRef}
                                        />
                                    ) : (
                                        <Navigate to="/accounts" />
                                    )
                                }
                            />
                            <Route
                                path="/accounts"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : (
                                        <AccountSettings
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                        />
                                    )
                                }
                            />
                            <Route
                                path="/groups"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : (
                                        <GroupManagement
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                        />
                                    )
                                }
                            />
                            <Route
                                path="/users"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : (
                                        <UserManagement
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                        />
                                    )
                                }
                            />
                            <Route
                                path="/settings"
                                element={
                                    mustSubscribe ? (
                                        <Navigate to="/subscriptions" />
                                    ) : (
                                        <Settings
                                            openLoading={openLoading}
                                            closeLoading={closeLoading}
                                            toggleNotifier={toggleNotifier}
                                        />
                                    )
                                }
                            />
                            <Route
                                path="/subscriptions"
                                element={
                                    <Subscriptions
                                        openLoading={openLoading}
                                        closeLoading={closeLoading}
                                        toggleNotifier={toggleNotifier}
                                        subscriptionPlan={subscriptionPlan}
                                        updateSubscriptionPlan={updateSubscriptionPlan}
                                    />
                                }
                            />
                            <Route path="*" element={<NoMatch />} />
                        </Routes>
                    </Suspense>
                </div>
            </div>
        </div>
    );
}

const withAuthenticatorOptions: WithAuthenticatorOptions = {
    signUpAttributes: ["email", "given_name", "family_name"],
    hideSignUp: !featureFlags.isSelfSignupEnabled()
};

export default withAuthenticator(App, withAuthenticatorOptions);
